import { googleEmulatorConfigSchema } from "./google-emulator.js";
import { z } from "zod";
import { boundedConnectorConfigString, connectorConfigScopeSchema, connectorOauthRefreshConfigShape, connectorConfigTimestampSchema, emptyConnectorConfigSchema, httpConnectorConfigUrl, userScopedConnectorSystemManagedConfigShape, } from "./connector-config-primitives.js";
function boundedInteger(min, max, label) {
    return z
        .number()
        .int(`${label} must be an integer`)
        .min(min, `${label} must be at least ${min}`)
        .max(max, `${label} must be at most ${max}`);
}
const googleConnectionShape = {
    googleSubjectId: boundedConnectorConfigString("googleSubjectId", 256).optional(),
    googleAccountEmail: z.string().trim().email().max(320).optional(),
    googleAccountName: boundedConnectorConfigString("googleAccountName", 512).optional(),
    pluginSlug: boundedConnectorConfigString("pluginSlug", 128).optional(),
    oauthConnectedAt: connectorConfigTimestampSchema.optional(),
    scope: connectorConfigScopeSchema.optional(),
};
function validateGoogleConnectionIdentity(value, ctx) {
    if (value.googleSubjectId && !value.googleAccountEmail) {
        ctx.addIssue({
            code: "custom",
            path: ["googleAccountEmail"],
            message: "googleAccountEmail is required with googleSubjectId",
        });
    }
    if (value.googleAccountEmail && !value.googleSubjectId) {
        ctx.addIssue({
            code: "custom",
            path: ["googleSubjectId"],
            message: "googleSubjectId is required with googleAccountEmail",
        });
    }
}
const gmailMutableShape = {
    query: boundedConnectorConfigString("query", 2_048).optional(),
    lookbackDays: boundedInteger(1, 3_650, "lookbackDays").optional(),
    includeBulk: z.boolean().optional(),
    includeSent: z.boolean().optional(),
    includeAttachments: z.boolean().optional(),
    pageSize: boundedInteger(1, 500, "pageSize").optional(),
    detailConcurrency: boundedInteger(1, 25, "detailConcurrency").optional(),
};
function validateGmailConfig(value, ctx) {
    if (value.query &&
        ["lookbackDays", "includeBulk", "includeSent"].some((key) => Object.hasOwn(value, key))) {
        ctx.addIssue({
            code: "custom",
            path: ["query"],
            message: "query cannot be combined with generated-query filters lookbackDays, includeBulk, or includeSent",
        });
    }
}
/** A shared mailbox (info@) owned by the organisation rather than a person. */
const gmailOwnershipShape = {
    connectorKind: z.literal("shared_mailbox").optional(),
};
const gmailCreateSchema = z.object({
    ...googleConnectionShape,
    ...gmailOwnershipShape,
    ...gmailMutableShape,
}).strict().superRefine((value, ctx) => {
    validateGoogleConnectionIdentity(value, ctx);
    validateGmailConfig(value, ctx);
});
// Custom query and generated-query filters are mutually exclusive. A shallow
// merge cannot remove the previous dialect, so switching dialect uses upsert.
const gmailUpdateSchema = z.object(gmailMutableShape).pick({
    includeAttachments: true,
    pageSize: true,
    detailConcurrency: true,
}).strict();
const gmailPersistedSchema = z.object({
    ...googleConnectionShape,
    ...gmailOwnershipShape,
    ...gmailMutableShape,
    ...connectorOauthRefreshConfigShape,
    ...userScopedConnectorSystemManagedConfigShape,
}).strict().superRefine((value, ctx) => {
    validateGoogleConnectionIdentity(value, ctx);
    validateGmailConfig(value, ctx);
});
export const gmailConnectorConfigSchemas = {
    create: z.union([gmailCreateSchema, googleEmulatorConfigSchema]),
    update: gmailUpdateSchema,
    persisted: z.union([gmailPersistedSchema, googleEmulatorConfigSchema]),
};
const calendarIdsSchema = boundedConnectorConfigString("calendarIds", 10_000)
    .superRefine((value, ctx) => {
    const ids = value.split(",").map((item) => item.trim());
    if (ids.some((id) => id.length === 0)) {
        ctx.addIssue({ code: "custom", message: "calendarIds contains an empty entry" });
    }
    if (ids.length > 500) {
        ctx.addIssue({ code: "custom", message: "calendarIds contains too many entries" });
    }
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: "custom", message: "calendarIds must not contain duplicates" });
    }
});
const googleCalendarMutableShape = {
    calendarIds: calendarIdsSchema.optional(),
    includeDeclined: z.boolean().optional(),
    rsvp: z.enum(["all", "accepted"]).optional(),
    pageSize: boundedInteger(1, 2_500, "pageSize").optional(),
    horizonDays: boundedInteger(1, 3_650, "horizonDays").optional(),
};
const nativeGoogleCalendarConnectorConfigSchemas = {
    create: z.object({
        ...googleConnectionShape,
        ...googleCalendarMutableShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
    update: z.object(googleCalendarMutableShape).strict(),
    persisted: z.object({
        ...googleConnectionShape,
        ...googleCalendarMutableShape,
        ...connectorOauthRefreshConfigShape,
        ...userScopedConnectorSystemManagedConfigShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
};
export const googleCalendarConnectorConfigSchemas = {
    ...nativeGoogleCalendarConnectorConfigSchemas,
    create: z.union([nativeGoogleCalendarConnectorConfigSchemas.create, googleEmulatorConfigSchema]),
    persisted: z.union([nativeGoogleCalendarConnectorConfigSchemas.persisted, googleEmulatorConfigSchema]),
};
const googleDriveMutableShape = {
    pageSize: boundedInteger(1, 1_000, "pageSize").optional(),
    includeTrashed: z.boolean().optional(),
    includeFolders: z.boolean().optional(),
    extractContent: z.boolean().optional(),
};
/*
 * Google Sheets is its own connection: the same Google sign-in, but the
 * things it syncs are spreadsheets (all of them, or a chosen list), not the whole Drive.
 */
const googleSheetsMutableShape = {
    /** Spreadsheets to sync, by Drive file id. Empty syncs every spreadsheet the account can see. */
    spreadsheetIds: z.array(z.string().trim().min(1).max(256)).max(200).optional(),
    /** Sync every spreadsheet the connected account can see even when spreadsheetIds is set. */
    syncAllSpreadsheets: z.boolean().optional(),
    /** Data rows read per tab (100,000 unless lowered); the rest of the tab is not projected. */
    maxRowsPerTab: boundedInteger(1, 100_000, "maxRowsPerTab").optional(),
    /** Spreadsheets read per ingestion page. */
    pageSize: boundedInteger(1, 100, "pageSize").optional(),
};
export const googleSheetsConnectorConfigSchemas = {
    create: z.object({
        ...googleConnectionShape,
        ...googleSheetsMutableShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
    update: z.object(googleSheetsMutableShape).strict(),
    persisted: z.object({
        ...googleConnectionShape,
        ...googleSheetsMutableShape,
        ...connectorOauthRefreshConfigShape,
        ...userScopedConnectorSystemManagedConfigShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
};
export const googleDriveConnectorConfigSchemas = {
    create: z.object({
        ...googleConnectionShape,
        ...googleDriveMutableShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
    update: z.object(googleDriveMutableShape).strict(),
    persisted: z.object({
        ...googleConnectionShape,
        ...googleDriveMutableShape,
        ...connectorOauthRefreshConfigShape,
        ...userScopedConnectorSystemManagedConfigShape,
    }).strict().superRefine(validateGoogleConnectionIdentity),
};
const outlookConnectionShape = {
    microsoftTenantId: boundedConnectorConfigString("microsoftTenantId", 256).optional(),
    microsoftUserId: boundedConnectorConfigString("microsoftUserId", 256).optional(),
    microsoftUserEmail: z.string().trim().email().max(320).optional(),
    microsoftUserName: boundedConnectorConfigString("microsoftUserName", 512).optional(),
    expiresAt: connectorConfigTimestampSchema.optional(),
    oauthConnectedAt: connectorConfigTimestampSchema.optional(),
    scope: connectorConfigScopeSchema.optional(),
    tokenType: boundedConnectorConfigString("tokenType", 128).optional(),
};
export const outlookMailConnectorConfigSchemas = {
    create: z.object(outlookConnectionShape).strict(),
    update: emptyConnectorConfigSchema,
    persisted: z.object({
        ...outlookConnectionShape,
        ...userScopedConnectorSystemManagedConfigShape,
        disconnectCleanupRequestedAt: connectorConfigTimestampSchema.optional(),
    }).strict(),
};
const githubRepositoryTuningShape = {
    apiBaseUrl: httpConnectorConfigUrl("apiBaseUrl", { originOnly: true }).optional(),
    apiVersion: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/u, "apiVersion must use GitHub's YYYY-MM-DD format").optional(),
    overlapHours: boundedInteger(0, 168, "overlapHours").optional(),
    repositoryPageSize: boundedInteger(1, 100, "repositoryPageSize").optional(),
    commitBranchScope: z.enum(["default", "all"]).optional(),
    maxPullRequestDetails: boundedInteger(1, 100, "maxPullRequestDetails").optional(),
    commitHistoryDays: boundedInteger(1, 3_650, "commitHistoryDays").optional(),
    pullRequestHistoryDays: boundedInteger(1, 3_650, "pullRequestHistoryDays").optional(),
    maxRepositoryPages: boundedInteger(1, 100, "maxRepositoryPages").optional(),
    maxEntityPages: boundedInteger(1, 100, "maxEntityPages").optional(),
};
const githubNotificationTuningShape = {
    scope: z.enum(["unread", "all"]).optional(),
    participating: z.boolean().optional(),
    pageSize: boundedInteger(1, 100, "pageSize").optional(),
    notificationOverlapHours: boundedInteger(0, 168, "notificationOverlapHours").optional(),
    notificationHistoryDays: boundedInteger(1, 3_650, "notificationHistoryDays").optional(),
    maxPages: boundedInteger(1, 1_000, "maxPages").optional(),
};
const githubInstallationIdentityShape = {
    installationId: z.string().trim().regex(/^\d+$/u, "installationId must contain digits only").max(32),
    accountLogin: boundedConnectorConfigString("accountLogin", 256),
    accountId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).optional(),
    accountType: boundedConnectorConfigString("accountType", 64).optional(),
    accountAvatarUrl: httpConnectorConfigUrl("accountAvatarUrl", {
        allowQuery: true,
    }).optional(),
    appSlug: boundedConnectorConfigString("appSlug", 256).optional(),
    repositorySelection: z.enum(["all", "selected"]).optional(),
    oauthConnectedAt: connectorConfigTimestampSchema.optional(),
};
const githubInstallationCreateSchema = z.object({
    connectorKind: z.literal("repositories"),
    authMode: z.literal("github-app-installation"),
    ...githubInstallationIdentityShape,
    ...githubRepositoryTuningShape,
}).strict();
const githubRepositoryStaticTokenCreateSchema = z.object({
    connectorKind: z.literal("repositories"),
    authMode: z.literal("static-token"),
    ...githubRepositoryTuningShape,
}).strict();
const githubNotificationCreateSchema = z.object({
    connectorKind: z.literal("notifications"),
    authMode: z.literal("static-token"),
    ...githubNotificationTuningShape,
}).strict();
const githubCreateSchema = z.union([
    githubInstallationCreateSchema,
    githubRepositoryStaticTokenCreateSchema,
    githubNotificationCreateSchema,
]);
const githubPersistedSchema = z.union([
    z.object({
        connectorKind: z.literal("repositories"),
        authMode: z.literal("github-app-installation"),
        ...githubInstallationIdentityShape,
        ...githubRepositoryTuningShape,
    }).strict(),
    z.object({
        connectorKind: z.literal("repositories"),
        authMode: z.literal("static-token"),
        ...githubRepositoryTuningShape,
    }).strict(),
    z.object({
        connectorKind: z.literal("notifications"),
        authMode: z.literal("static-token"),
        ...githubNotificationTuningShape,
        userContextId: boundedConnectorConfigString("userContextId", 512),
    }).strict(),
]);
export const githubConnectorConfigSchemas = {
    create: githubCreateSchema,
    update: z.discriminatedUnion("connectorKind", [
        z.object({
            connectorKind: z.literal("repositories"),
            ...githubRepositoryTuningShape,
        }).strict(),
        z.object({
            connectorKind: z.literal("notifications"),
            ...githubNotificationTuningShape,
        }).strict(),
    ]),
    persisted: githubPersistedSchema,
};
const granolaMutableShape = {
    apiBase: httpConnectorConfigUrl("apiBase", { httpsOrLoopback: true }).optional(),
    pageSize: boundedInteger(1, 30, "pageSize").optional(),
    maxPages: boundedInteger(1, 1_000, "maxPages").optional(),
    maxNotes: boundedInteger(1, 100_000, "maxNotes").optional(),
    includeTranscript: z.boolean().optional(),
    webhookScopes: z.union([
        z.tuple([z.literal("workspace")]),
        z.tuple([z.literal("personal")]),
        z.tuple([z.literal("public")]),
        z.tuple([z.literal("personal"), z.literal("public")]),
        z.tuple([z.literal("public"), z.literal("personal")]),
    ]).optional(),
};
const granolaCreateShape = {
    authMode: z.literal("native"),
    ...granolaMutableShape,
};
export const granolaConnectorConfigSchemas = {
    create: z.object(granolaCreateShape).strict(),
    update: z.object(granolaMutableShape).strict(),
    persisted: z.discriminatedUnion("authMode", [
        z.object({
            ...granolaCreateShape,
            ...userScopedConnectorSystemManagedConfigShape,
        }).strict(),
        z.object({
            authMode: z.literal("retired"),
            retirementReason: z.literal("granola-composio-ingestion-retired"),
            retiredAt: connectorConfigTimestampSchema,
            ...userScopedConnectorSystemManagedConfigShape,
        }).strict(),
    ]),
};
const slackMutableShape = {
    mentionChannelPageSize: boundedInteger(1, 1_000, "mentionChannelPageSize").optional(),
    dmPageSize: boundedInteger(1, 1_000, "dmPageSize").optional(),
    mentionHistoryLimit: boundedInteger(1, 200, "mentionHistoryLimit").optional(),
    dmHistoryLimit: boundedInteger(1, 200, "dmHistoryLimit").optional(),
    slackOverlapHours: boundedInteger(0, 168, "slackOverlapHours").optional(),
    excludeSelf: z.boolean().optional(),
    excludeSelfDms: z.boolean().optional(),
    mentionQuery: boundedConnectorConfigString("mentionQuery", 512).optional(),
    mentionHistoryPages: boundedInteger(1, 20, "mentionHistoryPages").optional(),
    dmHistoryPages: boundedInteger(1, 50, "dmHistoryPages").optional(),
    maxMentionConversations: boundedInteger(1, 500, "maxMentionConversations").optional(),
    maxDmConversations: boundedInteger(1, 200, "maxDmConversations").optional(),
    mentionPagesPerRun: boundedInteger(1, 10_000, "mentionPagesPerRun").optional(),
    dmPagesPerRun: boundedInteger(1, 10_000, "dmPagesPerRun").optional(),
    bootstrapLookbackDays: boundedInteger(1, 3_650, "bootstrapLookbackDays").optional(),
};
const slackConnectionShape = {
    scope: connectorConfigScopeSchema,
    teamId: boundedConnectorConfigString("teamId", 256).optional(),
    teamName: boundedConnectorConfigString("teamName", 512).optional(),
    slackUserId: boundedConnectorConfigString("slackUserId", 256).optional(),
    oauthConnectedAt: connectorConfigTimestampSchema.optional(),
    tokenType: boundedConnectorConfigString("tokenType", 128).optional(),
};
export const slackConnectorConfigSchemas = {
    create: z.object({ ...slackConnectionShape, ...slackMutableShape }).strict(),
    update: z.object(slackMutableShape).strict(),
    persisted: z.object({
        ...slackConnectionShape,
        ...slackMutableShape,
        ...userScopedConnectorSystemManagedConfigShape,
    }).strict(),
};
